import {
  browserSupportsWebAuthn, startAuthentication, startRegistration, WebAuthnAbortService,
  type PublicKeyCredentialCreationOptionsJSON, type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { record, type KratosFlow, type KratosNode } from './flows'

// Kratos v26.2.0 selfservice/strategy/passkey/{passkey_login,passkey_settings}.go.
// Read only the typed challenge nodes; never execute upstream script/onclick.
export function passkeyNodes(flow: KratosFlow): KratosNode[] {
  return flow.ui.nodes.filter(node => node.type === 'input' && node.group === 'passkey')
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && browserSupportsWebAuthn()
}

function challenge(flow: KratosFlow, name: string): Record<string, unknown> {
  const node = passkeyNodes(flow).find(node => node.attributes.name === name && !node.attributes.disabled)
  if (typeof node?.attributes.value !== 'string') throw new Error('The Passkey form is incomplete. Please start again.')
  try {
    const options = record(record(JSON.parse(node.attributes.value)).publicKey)
    if (typeof options.challenge !== 'string' || !options.challenge) throw new Error('Missing challenge')
    return options
  } catch {
    throw new Error('The Passkey form is incomplete. Please start again.')
  }
}

function csrfData(flow: KratosFlow): Record<string, string> {
  const csrf = flow.ui.nodes.find(node => node.type === 'input' && node.group === 'default' &&
    node.attributes.name === 'csrf_token' && !node.attributes.disabled)
  if (typeof csrf?.attributes.value !== 'string' || !csrf.attributes.value) {
    throw new Error('The Passkey form is incomplete. Please start again.')
  }
  return { method: 'passkey', csrf_token: csrf.attributes.value }
}

function browserError(error: unknown): Error {
  const name = record(record(error).cause).name || record(error).name
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return new Error('Passkey request cancelled or timed out. Try again or use your password.')
  }
  if (name === 'InvalidStateError') return new Error('This Passkey is already registered. Use another authenticator.')
  if (name === 'SecurityError') return new Error('Passkeys are unavailable on this address. Use your password and contact support.')
  return new Error('Unable to use this Passkey. Try again or use your password.')
}

export async function passkeySubmission(flow: KratosFlow, action: 'login' | 'register', signal: AbortSignal): Promise<Record<string, string>> {
  const data = csrfData(flow)
  const options = challenge(flow, action === 'login' ? 'passkey_challenge' : 'passkey_create_data')
  if (!passkeysSupported()) throw new Error('This browser cannot use Passkeys here. You can still use your password.')
  if (signal.aborted) throw new Error('Passkey request cancelled or timed out. Try again or use your password.')
  const cancel = () => WebAuthnAbortService.cancelCeremony()
  signal.addEventListener('abort', cancel, { once: true })
  try {
    const credential = action === 'login'
      ? await startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON })
      : await startRegistration({ optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON })
    if (signal.aborted) throw new Error('Aborted')
    return { ...data, [action === 'login' ? 'passkey_login' : 'passkey_settings_register']: JSON.stringify(credential) }
  } catch (error) {
    throw browserError(error)
  } finally {
    signal.removeEventListener('abort', cancel)
  }
}

export function passkeyRemoval(flow: KratosFlow, node: KratosNode): Record<string, string> {
  if (!passkeyNodes(flow).includes(node) || node.attributes.name !== 'passkey_remove' ||
      node.attributes.type !== 'submit' || node.attributes.disabled || typeof node.attributes.value !== 'string') {
    throw new Error('The Passkey form is incomplete. Please start again.')
  }
  return { ...csrfData(flow), passkey_remove: node.attributes.value }
}
